import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../shared/services/auth.service';

const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');

  if (!password || !confirmPassword) {
    return null;
  }

  return password.value === confirmPassword.value ? null : { passwordsMismatch: true };
};

@Component({
  selector: 'app-reset-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule
  ],
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.scss'
})
export class ResetPassword implements OnInit {
  resetForm: FormGroup;
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);
  isLoading = signal<boolean>(false);
  isCheckingCode = signal<boolean>(true);
  isCodeValid = signal<boolean>(false);
  userEmail = signal<string | null>(null);
  hidePassword = signal<boolean>(true);
  hideConfirmPassword = signal<boolean>(true);

  private oobCode: string | null = null;
  private mode: string | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    this.resetForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: passwordMatchValidator });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(async (params) => {
      this.mode = params['mode'];
      this.oobCode = params['oobCode'];

      if (!this.oobCode || this.mode !== 'resetPassword') {
        this.errorMessage.set('Il link per il ripristino della password non è valido o è scaduto.');
        this.isCheckingCode.set(false);
        this.isCodeValid.set(false);
        return;
      }

      try {
        this.isLoading.set(true);
        const email = await this.authService.verifyPasswordResetCode(this.oobCode);
        this.userEmail.set(email);
        this.isCodeValid.set(true);
      } catch (error: any) {
        console.error('Error verifying action code:', error);
        this.errorMessage.set('Il link per il ripristino della password non è più valido. Potrebbe essere già stato utilizzato o essere scaduto.');
        this.isCodeValid.set(false);
      } finally {
        this.isLoading.set(false);
        this.isCheckingCode.set(false);
      }
    });
  }

  async onSubmit(): Promise<void> {
    if (this.resetForm.invalid || !this.oobCode) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    const { password } = this.resetForm.value;

    try {
      await this.authService.confirmPasswordReset(this.oobCode, password);
      this.successMessage.set('La tua password è stata modificata con successo. Ora puoi accedere con la nuova password.');
    } catch (error: any) {
      console.error('Password reset confirmation error:', error);
      let errorMsg = 'Impossibile ripristinare la password.';
      if (error.code === 'auth/expired-action-code') {
        errorMsg = 'Il codice per il ripristino della password è scaduto.';
      } else if (error.code === 'auth/invalid-action-code') {
        errorMsg = 'Il codice per il ripristino della password non è valido.';
      } else if (error.code === 'auth/weak-password') {
        errorMsg = 'La nuova password scelta è troppo debole.';
      } else {
        errorMsg = error.message || errorMsg;
      }
      this.errorMessage.set(errorMsg);
    } finally {
      this.isLoading.set(false);
    }
  }

  async navigateToLogin(): Promise<void> {
    await this.router.navigate(['/login']);
  }
}
